import React, { useCallback } from "react";
import {
  Text,
  Box,
  Flex,
  Center,
  Divider,
  Link,
  Button,
  Show,
} from "@chakra-ui/react";

import { ModalDrawer } from "dapp-sdk-v2";

import { TextPrimary, TextSecondary } from "theme";
import {
  CertoraColored,
  ChervonRight,
  FortaColored,
  HalbornColored,
  ImmunefiColored,
  PeckshieldColored,
  VerifiedUser,
  Immunebytes,
  Sigmaprime,
  CodeArenaColored,
} from "icons";

import { StrapiAuditsData } from "@customtypes/global";
import { IIndexable } from "@customtypes/common";

import Icon from "@components/Icon";

interface WalletMenuType {
  isOpen: boolean;
  closeModal(): void;
  title?: string;
  subTitle?: string;
  auditsData: StrapiAuditsData[];
  actionCTA?: string;
  actionLink?: string;
  bugBountySection?: {
    title: string;
    sub_title?: string;
    audits_data: StrapiAuditsData[];
  };
}

const AuditsIconMapping: IIndexable = {
  halborn: HalbornColored,
  peckshield: PeckshieldColored,
  certora: CertoraColored,
  forta: FortaColored,
  immunefi: ImmunefiColored,
  immunebytes: Immunebytes,
  sigmaprime: Sigmaprime,
  codearena: CodeArenaColored,
};

const AuditsModal = ({
  isOpen,
  title,
  subTitle,
  auditsData,
  bugBountySection,
  actionCTA,
  actionLink,
  closeModal,
}: WalletMenuType) => {
  const getAuditsData = useCallback(
    (auditsData: StrapiAuditsData[]) => (
      <>
        {auditsData &&
          auditsData.map((data) => (
            <React.Fragment key={data.key}>
              <Show above="md">
                <a
                  href={data?.link ? data.link : undefined}
                  target="_blank"
                  rel="noreferrer"
                  key={data.key}
                >
                  <Box
                    bg="backgroundPrimary"
                    width={{ base: "100%", md: "224px" }}
                    outline="none"
                    p="0"
                    h="60px"
                    _focusVisible={{
                      outline: "0px",
                    }}
                    borderRadius="0.5rem"
                  >
                    <Flex
                      width="100%"
                      justifyContent="space-between"
                      alignItems="center"
                      p="1rem 1.5rem"
                    >
                      <Flex alignItems="center">
                        <Icon
                          width="32px"
                          height="32px"
                          Icon={AuditsIconMapping[data.key.toLowerCase()]}
                        />
                        <Text fontSize="16px" fontWeight="600" ml="1rem">
                          {data.title}
                        </Text>
                      </Flex>

                      <Box
                        color="primary"
                        _dark={{
                          color: "textPrimary",
                        }}
                      >
                        <Icon width="24px" height="24px" Icon={ChervonRight} />
                      </Box>
                    </Flex>
                  </Box>
                </a>
              </Show>
              <Show below="md">
                <a
                  href={data?.link ? data.link : undefined}
                  target="_blank"
                  rel="noreferrer"
                  key={data.key}
                >
                  <Flex
                    width="100%"
                    gap="4px"
                    color="primary"
                    // justifyContent="space-between"
                    alignItems="center"
                  >
                    <Text fontSize="16px" fontWeight="600" ml="1rem">
                      {data.title}
                    </Text>

                    <Box
                      color="primary"
                      _dark={{
                        color: "textPrimary",
                      }}
                    >
                      <Icon width="16px" height="16px" Icon={ChervonRight} />
                    </Box>
                  </Flex>
                </a>
              </Show>
            </React.Fragment>
          ))}
      </>
    ),
    []
  );

  return (
    <ModalDrawer
      isOpen={isOpen}
      closeAlert={() => closeModal()}
      bodyProps={{ mt: "1.5rem" }}
      header={
        <Center flexDir="column" justifyContent="center">
          <Text width="100%">{title}</Text>
          {subTitle && (
            <TextSecondary mt="8px" fontSize="16px" fontWeight="500">
              {subTitle}
            </TextSecondary>
          )}
          <Box
            mt="1rem"
            color="primary"
            _dark={{
              color: "textPrimary",
            }}
          >
            <Icon height="80px" width="80px" Icon={VerifiedUser} />
          </Box>
        </Center>
      }
      contentProps={{
        p: { base: "2.5rem 1rem 1rem", md: "2rem 2rem" },
        overflow: "auto",
        maxH: "90%",
      }}
      headerProps={{
        p: "0",
        fontSize: "1.5rem",
        fontWeight: "600",
        textAlign: "center",
      }}
    >
      <>
        <Flex
          flexWrap="wrap"
          gap="1rem"
          alignItems="center"
          justifyContent="center"
          width="100%"
        >
          {getAuditsData(auditsData)}
        </Flex>

        {bugBountySection ? (
          <>
            <Divider bg="seperator" mt="1rem" />
            <TextPrimary my="1rem" fontWeight="500" textAlign="center">
              {bugBountySection.title}
            </TextPrimary>
            <Flex
              flexWrap="wrap"
              gap="1rem"
              alignItems="center"
              justifyContent="center"
              width="100%"
            >
              {getAuditsData(bugBountySection.audits_data)}
            </Flex>
          </>
        ) : null}

        {actionCTA && actionLink ? (
          <Center>
            <Link
              href={actionLink}
              isExternal={true}
              // onClick={() => onAuditsViewMoreClick()}
            >
              <Button
                mt="1.5rem"
                variant="none"
                h="48px"
                background="transparent"
                border="1px solid"
                borderColor="seperator"
                borderRadius="6px"
                p="8px 45px"
                w="187px"
                color="textPrimary"
              >
                <Text
                  fontWeight="600" // color="textPrimary"
                >
                  {actionCTA}
                </Text>
              </Button>
            </Link>
          </Center>
        ) : null}
      </>
    </ModalDrawer>
  );
};

export default AuditsModal;
